import React, { useState, useEffect } from 'react';
import './App.css';
import { Rule, Notification } from './types';
import { RuleGroupType } from 'react-querybuilder';
import RuleBuilder from './components/RuleBuilder';
import RuleList from './components/RuleList';
import ImportExport from './components/ImportExport';
import NotificationPreview from './components/NotificationPreview';
import { defaultFilters } from './data/filters';
import { defaultTriggers } from './data/triggers';
import { sampleRules } from './data/sampleRules';
import { v4 as uuidv4 } from 'uuid';
import { Plus, List, Upload, Eye } from 'lucide-react';

type ViewMode = 'list' | 'create' | 'edit' | 'import' | 'preview';

function App() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [previewNotification] = useState<Notification | null>(null);

  useEffect(() => {
    const savedRules = localStorage.getItem('ruleEngineRules');
    if (savedRules) {
      const parsed = JSON.parse(savedRules);
      setRules(parsed.map((rule: any) => ({
        ...rule,
        createdAt: new Date(rule.createdAt),
        updatedAt: new Date(rule.updatedAt)
      })));
    } else {
      setRules(sampleRules);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('ruleEngineRules', JSON.stringify(rules));
  }, [rules]);

  const handleSaveRule = (filters: RuleGroupType, triggers: RuleGroupType, notification: Notification) => {
    if (editingRule) {
      const updatedRules = rules.map(rule => 
        rule.id === editingRule.id 
          ? {
              ...rule,
              filters,
              triggers,
              notification,
              updatedAt: new Date()
            }
          : rule
      );
      setRules(updatedRules);
    } else {
      const ruleName = prompt('Enter a name for this rule:');
      if (!ruleName) return;

      const ruleDescription = prompt('Enter a description (optional):');

      const newRule: Rule = {
        id: uuidv4(),
        name: ruleName,
        description: ruleDescription || undefined,
        filters,
        triggers,
        notification,
        active: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      setRules([...rules, newRule]);
    }
    
    setViewMode('list');
    setEditingRule(null);
  };

  const handleEditRule = (rule: Rule) => {
    setEditingRule(rule);
    setViewMode('edit');
  };

  const handleDeleteRule = (id: string) => {
    setRules(rules.filter(rule => rule.id !== id));
  };

  const handleToggleRule = (id: string) => {
    setRules(rules.map(rule => 
      rule.id === id 
        ? { ...rule, active: !rule.active, updatedAt: new Date() }
        : rule
    ));
  };

  const handleDuplicateRule = (rule: Rule) => {
    const newRule: Rule = {
      ...rule,
      id: uuidv4(),
      name: `${rule.name} (Copy)`,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setRules([...rules, newRule]);
  };

  const handleImportRules = (importedRules: Rule[]) => {
    const newRules = importedRules.map(rule => ({
      ...rule,
      id: uuidv4(),
      createdAt: new Date(),
      updatedAt: new Date()
    }));
    setRules([...rules, ...newRules]);
    setViewMode('list');
  };

  // const handlePreviewNotification = (notification: Notification) => {
  //   setPreviewNotification(notification);
  //   setViewMode('preview');
  // };

  const handleCancel = () => {
    setEditingRule(null);
    setViewMode('list');
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🐕🐈 Pet Care Rule Engine</h1>
        <p>Create intelligent rules for dog and cat care notifications</p>
      </header>

      <nav className="nav-tabs">
        <button 
          className={viewMode === 'list' ? 'active' : ''}
          onClick={() => setViewMode('list')}
        >
          <List size={16} />
          Rules List
        </button>
        <button 
          className={viewMode === 'create' || viewMode === 'edit' ? 'active' : ''}
          onClick={() => {
            setEditingRule(null);
            setViewMode('create');
          }}
        >
          <Plus size={16} />
          Create Rule
        </button>
        <button 
          className={viewMode === 'import' ? 'active' : ''}
          onClick={() => setViewMode('import')}
        >
          <Upload size={16} />
          Import/Export
        </button>
        {previewNotification && (
          <button 
            className={viewMode === 'preview' ? 'active' : ''}
            onClick={() => setViewMode('preview')}
          >
            <Eye size={16} />
            Preview
          </button>
        )}
      </nav>

      <main className="main-content">
        {viewMode === 'list' && (
          <>
            <div className="stats-bar">
              <div className="stat">
                <span className="stat-value">{rules.length}</span>
                <span className="stat-label">Total Rules</span>
              </div>
              <div className="stat">
                <span className="stat-value">{rules.filter(r => r.active).length}</span>
                <span className="stat-label">Active Rules</span>
              </div>
              <div className="stat">
                <span className="stat-value">{defaultFilters.length}</span>
                <span className="stat-label">Available Filters</span>
              </div>
              <div className="stat">
                <span className="stat-value">{defaultTriggers.length}</span>
                <span className="stat-label">Available Triggers</span>
              </div>
            </div>
            <RuleList
              rules={rules}
              onEdit={handleEditRule}
              onDelete={handleDeleteRule}
              onToggle={handleToggleRule}
              onDuplicate={handleDuplicateRule}
            />
          </>
        )}

        {(viewMode === 'create' || viewMode === 'edit') && (
          <RuleBuilder
            filters={defaultFilters}
            triggers={defaultTriggers}
            initialFilters={editingRule?.filters}
            initialTriggers={editingRule?.triggers}
            initialNotification={editingRule?.notification}
            onSave={handleSaveRule}
            onCancel={handleCancel}
          />
        )}

        {viewMode === 'import' && (
          <ImportExport
            rules={rules}
            onImport={handleImportRules}
          />
        )}

        {viewMode === 'preview' && previewNotification && (
          <NotificationPreview notification={previewNotification} />
        )}
      </main>

      <footer className="App-footer">
        <p>Rule Engine v1.0.0 | Using JSON Logic Format</p>
      </footer>
    </div>
  );
}

export default App;
